import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import { Controller, useForm } from 'react-hook-form'

import {
  Box,
  TextField,
  Grid,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { BaseDialog, Select, Label } from 'components'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'

import constants from 'constants/index'
import * as service from 'service'

const DialogAdd = ({
  open,
  setOpen = () => {},
  isCompanySelfPresent,
  refresh = () => {},
  dataProcessId,
  agentsNameds,
}) => {
  const [radioValue, setRadioValue] = useState(null)

  const snackbar = useSnackbar()

  const { control, errors, setValue, handleSubmit, watch } = useForm({
    defaultValues: {
      agentType: '',
      name: '',
      document: '',
    },
  })

  const {
    CONTROLLER_TYPE,
    OPERATOR_TYPE,
    CONTROLLER_LABEL,
    OPERATOR_LABEL,
    CO_CONTROLLER_TYPE,
    CO_CONTROLLER_LABEL,
    SUBOPERATOR_TYPE,
    SUBOPERATOR_LABEL,
  } = constants.dataTreatmentAgents

  const { response } = useFetch(
    service.dponet.dataTreatmentAgents.listOptions,
    { dataProcessId },
    [],
  )

  const dataTreatmentAgents = response?.data || []

  const handleCreate = (agent) =>
    service.dponet.dataTreatmentAgents.create({
      dataProcessId,
      not_validation: true,
      agents: [agent],
    })

  const onSubmit = async (data) => {
    const documentAlreadyExists = agentsNameds.includes(data.name)

    if (documentAlreadyExists) {
      return snackbar.open({
        message: `O agente ${data.name} já está vinculado a um agente de tratamento neste processo!`,
        variant: 'error',
      })
    }

    try {
      if (!isCompanySelfPresent) {
        await handleCreate({ agentType: radioValue, companySelf: true })
        setRadioValue(null)
        refresh()
      } else {
        await handleCreate(data)
        refresh()
        setOpen(false)
      }

      snackbar.open({
        message: 'Agente adicionado com sucesso!',
        variant: 'success',
      })
    } catch (error) {
      setRadioValue(null)
      setOpen(false)
      snackbar.open({
        message: 'Erro ao adicionar o agente!',
        variant: 'error',
      })
    }
  }

  const getTreatmentAgentByEnum = (treatmentAgentEnum) => {
    if (!treatmentAgentEnum) return

    const treatmentAgents = constants.dataTreatmentAgents.OPTIONS_TYPES

    return treatmentAgents.find(
      (treatmentAgent) =>
        treatmentAgent.id === treatmentAgentEnum.toUpperCase(),
    )
  }

  useEffect(() => {
    const searchAgent = watch('searchAgent')

    if (searchAgent) {
      setValue('document', searchAgent.document)
      setValue('name', searchAgent.name)

      if (searchAgent.treatmentAgentType) {
        const agentType = getTreatmentAgentByEnum(
          searchAgent.treatmentAgentType,
        )?.id

        setValue('agentType', agentType)
      } else {
        setValue('agentType', '')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('searchAgent')])

  return (
    <form onSubmit={handleSubmit(onSubmit)} id="agent-edit-form">
      <BaseDialog
        dialogSize="sm"
        open={open}
        setOpen={setOpen}
        closeButtonText="Cancelar"
        actionButtonText="Cadastrar"
        formRef="agent-edit-form"
        title="Adicionar novo agente"
      >
        <>
          {!isCompanySelfPresent ? (
            <Box>
              <Box mb={1}>
                <Typography variant="body1">
                  Antes de adicionar os agentes selecione o seu papel nesse
                  processo
                </Typography>
              </Box>
              <RadioGroup
                onClick={(event) => setRadioValue(event.target.value)}
                defaultValue={CONTROLLER_TYPE}
              >
                <FormControlLabel
                  value={CONTROLLER_TYPE}
                  control={<Radio />}
                  label={CONTROLLER_LABEL}
                  checked={radioValue === CONTROLLER_TYPE}
                />
                <FormControlLabel
                  value={CO_CONTROLLER_TYPE}
                  control={<Radio />}
                  label={CO_CONTROLLER_LABEL}
                  checked={radioValue === CO_CONTROLLER_TYPE}
                />
                <FormControlLabel
                  value={OPERATOR_TYPE}
                  control={<Radio />}
                  label={OPERATOR_LABEL}
                  checked={radioValue === OPERATOR_TYPE}
                />
                <FormControlLabel
                  value={SUBOPERATOR_TYPE}
                  control={<Radio />}
                  label={SUBOPERATOR_LABEL}
                  checked={radioValue === SUBOPERATOR_TYPE}
                />
              </RadioGroup>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {!isEmpty(dataTreatmentAgents) && (
                <Grid item xs={12}>
                  <Label title="Buscar organizações">
                    <Box mb={2}>
                      <Controller
                        control={control}
                        name="searchAgent"
                        onChange={([, data]) => data}
                        mode="onChange"
                        as={
                          <Autocomplete
                            options={dataTreatmentAgents}
                            getOptionLabel={(option) => option.name}
                            getOptionSelected={(option, value) =>
                              option.name === value.name || value.name === 0
                            }
                            renderInput={(params) => (
                              <TextField {...params} variant="outlined" />
                            )}
                          />
                        }
                      />
                    </Box>
                  </Label>
                </Grid>
              )}
              <Grid item xs={12}>
                <Label
                  title={
                    isEmpty(response?.data) ? '' : 'Adicionar nova organização'
                  }
                >
                  <Controller
                    as={
                      <TextField
                        label="Organização"
                        color="primary"
                        variant="outlined"
                        error={!!errors?.name}
                        helperText={errors?.name && 'Preencha este campo'}
                        fullWidth
                      />
                    }
                    control={control}
                    rules={{ required: isCompanySelfPresent }}
                    name="name"
                    mode="onBlur"
                  />
                </Label>
              </Grid>
              <Grid item xs={12}>
                <Controller
                  as={
                    <TextField
                      inputProps={{
                        maxLength: 18,
                      }}
                      label="CPF/CNPJ"
                      color="primary"
                      variant="outlined"
                      error={!!errors?.document}
                      helperText={errors?.document && 'Preencha este campo'}
                      fullWidth
                    />
                  }
                  control={control}
                  name="document"
                  mode="onBlur"
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  as={
                    <Select
                      items={constants.dataTreatmentAgents.OPTIONS_TYPES}
                      label="Tipo"
                      error={!!errors?.agentType}
                      helperText={errors?.agentType && 'Preencha este campo'}
                      fullWidth
                    />
                  }
                  control={control}
                  rules={{ required: isCompanySelfPresent }}
                  name="agentType"
                  mode="onBlur"
                />
              </Grid>
            </Grid>
          )}
        </>
      </BaseDialog>
    </form>
  )
}

DialogAdd.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  isCompanySelfPresent: PropTypes.bool,
  refresh: PropTypes.func,
  dataProcessId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default DialogAdd
